import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useAtualizaConfigRelatorio } from '@/services/Relatorios';

interface ModalDescricaoRegraProps {
  aberto: boolean;
  onFechar: () => void;
  titulo: string;
  descricao: string;
  relatorioId: number;
}

export function ModalDescricaoRegra({
  aberto,
  onFechar,
  titulo,
  descricao,
  relatorioId,
}: ModalDescricaoRegraProps) {
  const [descricaoEditada, setDescricaoEditada] = useState(descricao);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutate: atualizarConfig, isPending: isAtualizando } = useAtualizaConfigRelatorio();

  useEffect(() => {
    if (aberto) {
      setDescricaoEditada(descricao);
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
    return undefined;
  }, [aberto, descricao]);

  const handleSalvar = () => {
    atualizarConfig(
      {
        relatorio_id: relatorioId,
        Descricao_Regra: descricaoEditada,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['relatorios', 'listar'] });
          toast({
            title: 'Sucesso',
            description: 'Descrição da regra atualizada com sucesso.',
          });
          onFechar();
        },
        onError: error => {
          toast({
            variant: 'destructive',
            title: 'Erro',
            description: error.message || 'Erro ao atualizar descrição da regra.',
          });
        },
      }
    );
  };

  if (!aberto) return null;

  const content = (
    <>
      <div className="fixed inset-0 z-9999 bg-black/80 backdrop-blur-sm" onClick={onFechar} />
      <div className="fixed inset-0 z-10000 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="w-[95vw] sm:w-[70vw] max-w-none h-[90vh] bg-background rounded-lg shadow-2xl overflow-hidden flex flex-col pointer-events-auto"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div>
              <h2 className="text-lg font-semibold leading-none tracking-tight">{titulo}</h2>
              <p className="text-sm text-muted-foreground mt-1.5">
                Descrição da regra do relatório
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onFechar} className="hover:bg-accent">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <Textarea
              value={descricaoEditada}
              onChange={e => setDescricaoEditada(e.target.value)}
              className="h-full min-h-[500px] resize-none font-mono text-sm"
            />
          </div>

          {/* Footer */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-6 py-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onFechar}
              className="flex-1 sm:flex-none"
            >
              Fechar
            </Button>
            <Button
              type="button"
              onClick={handleSalvar}
              className="flex-1 sm:flex-none"
              disabled={isAtualizando}
            >
              {isAtualizando ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
}
